import React, { useEffect, useRef, useState } from "react";
import { Progress, Checkbox, Table, Button, message } from "antd";
import { GetRegionsList } from "../../API/endpoints/Regions";
import { EditBrandInfo, SaveBrandDigitalInfo, SaveBrandInfo, updateBrandDigitalInfo, UpdateBrandPreference } from "../../API/endpoints/Brands";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { marketOptions } from "../../constants/dataConstants";

const countries = [
  { name: "Singapore", progress: 4, total: 11 },
  { name: "Malaysia", progress: 4, total: 11 },
];

interface Props {
  setDefaultTab: any;
  brandID: string;
  brandName: string
  selectedEngagementModel: string;
  selectedExclusivityTenure: string;
  selectedFobCifPreference: string;
  selectedMarketingInvestment: string;
  setBrandId: (brandId: string) => void;
  expansionSetupData: any;
  healthChecked: any;
  selectedHealthOptions: any;
  beautyChecked: any;
  selectedBeautyOptions: any;
  foodChecked: any;
  selectedFoodOptions: any;
  petChecked: any;
  selectedPetOptions: any;
  selectYearsOperations: any;
  currentRevenueStatus: string;
  selectOrg: string;
  socialLinks: any;
  marketplaces: any;
}

const ExpansionSetup = (props: Props) => {
  const {
    brandName,
    selectOrg,
    socialLinks,
    marketplaces,
    selectedEngagementModel,
    selectedExclusivityTenure,
    selectedFobCifPreference,
    selectedMarketingInvestment,
    expansionSetupData,
    healthChecked,
    selectedHealthOptions,
    beautyChecked,
    selectedBeautyOptions,
    foodChecked,
    selectedFoodOptions,
    petChecked,
    selectedPetOptions,
    selectYearsOperations,
    currentRevenueStatus,
    setBrandId
  } = props;
  const navigate = useNavigate();
  const pathname = useLocation();
  const params = useParams();
  const [selectedCountry, setSelectedCountry] = useState("Singapore");
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [marketList, setMarketList] = useState([]);

  useEffect(() => {
    if (expansionSetupData.length && marketList.length) {
      const newSelectedMarkets: { [key: string]: boolean } = {};
      const newCheckedItems: { [key: string]: boolean } = {};

      expansionSetupData.forEach((pref: any) => {
        const region: any = marketList.find((r: any) => r.id === pref.region);
        if (!region) return;

        const regionName = region.name;
        newSelectedMarkets[regionName] = true;

        if (pref.offline_retail)
          newCheckedItems[`${regionName}-Offline Retail`] = true;
        if (pref.online) newCheckedItems[`${regionName}-Online`] = true;
        if (pref.food_service)
          newCheckedItems[`${regionName}-Food Service/Out of Home`] = true;

        if (pref.regulatory_compliance)
          newCheckedItems[`${regionName}-Regulatory Compliance`] = true;
        if (pref.importation_clearance)
          newCheckedItems[`${regionName}-Importation & Clearance`] = true;

        if (pref.ambient_warehousing)
          newCheckedItems[`${regionName}-Ambient`] = true;
        if (pref.cold_and_frozen_warehousing)
          newCheckedItems[`${regionName}-Cold & Frozen`] = true;

        if (pref.retail_distribution)
          newCheckedItems[`${regionName}-Distribution`] = true;
        if (pref.sales) newCheckedItems[`${regionName}-Sales`] = true;
        if (pref.marketing) newCheckedItems[`${regionName}-Marketing`] = true;
      });

      setSelectedMarkets(newSelectedMarkets);
      setCheckedItems(newCheckedItems);
    }
  }, [expansionSetupData, marketList]);

  const [selectedMarkets, setSelectedMarkets] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    GetRegionsList()
      .then((res: any) => {
        const fetchedMarkets = res?.data || [];
        setMarketList(fetchedMarkets);

        // Init check state
        const initMarketChecks = fetchedMarkets.reduce(
          (acc: any, region: any) => ({ ...acc, [region.name]: false }),
          {}
        );
        setSelectedMarkets(initMarketChecks);
      })
      .catch((err: any) => {
        console.error("Something went wrong:", err);
      });
  }, []);

  const handleSaveAndContinue = async () => {
    if (selectedMarkets && !Object.entries(selectedMarkets).some((item: any[]) => item[1] === true && hasAnyChecked(item[0]))) {
      message.warning("Please select atleast one market")
      return;
    }

    const categoriesSelected = [
      {
        checked: healthChecked,
        selected: selectedHealthOptions,
        label: "Health Supplements",
      },
      {
        checked: beautyChecked,
        selected: selectedBeautyOptions,
        label: "Beauty & Skin",
      },
      {
        checked: foodChecked,
        selected: selectedFoodOptions,
        label: "Functional Food & Beverages",
      },
      {
        checked: petChecked,
        selected: selectedPetOptions,
        label: "Pet Nutrition",
      },
    ];

    const focus_categories = [
      {
        category: "Health Supplements",
        sub_category: selectedHealthOptions,
      },
      { category: "Beauty & Skin", sub_category: selectedBeautyOptions },
      {
        category: "Functional Food & Beverages",
        sub_category: selectedFoodOptions,
      },
      { category: "Pet Nutrition", sub_category: selectedPetOptions },
    ].filter((item) => item.sub_category.length > 0); // Only keep if subcategories selected

    const orgId = params?.orgid || "";

    const brandInfo = {
      org_id: pathname?.pathname === "/addnewbrand" ? selectOrg : orgId,
      brand_name: brandName.trim(),
      years_of_operation: selectYearsOperations,
      current_revenue: currentRevenueStatus.trim(),
      focus_categories: focus_categories,
    };
    if (params?.brandId) {
      EditBrandInfo(brandInfo, params?.brandId)
        .then(async (res: any) => {
          props.setDefaultTab("engagement-terms");
          // Filtering out default or empty values
          const cleanedSocialLinks = socialLinks.filter(
            (link: any) => link.url.trim() !== ""
          );

          const cleanedMarketplaces = marketplaces.filter(
            (marketplace: any) => marketplace.url.trim() !== ""
          );
          const brandDigitalInfo = {
            social_media: cleanedSocialLinks,
            market_place: cleanedMarketplaces,
          };
          const digitalInfoRes = await updateBrandDigitalInfo(params?.brandId, brandDigitalInfo);
          handleSaveBrandPreference(params?.brandId)
        })
        .catch((err: any) => {
          console.error("Something went wrong:", err);
        });
    } else {
      // Filter categories where the checkbox is checked
      const selectedCategories = categoriesSelected.filter(
        (cat) => cat.checked
      );

      if (selectedCategories.length === 0) {
        message.error("Please select at least one category before continuing.");
        return;
      }

      // Validate each checked category has at least one selected sub-category
      for (const cat of selectedCategories) {
        if (cat.selected.length === 0) {
          message.error(
            `Please select at least one sub-category in ${cat.label}.`
          );
          return;
        }
      }

      try {
        // Save brand info
        const brandInfoRes = await SaveBrandInfo(brandInfo);
        const brandId = brandInfoRes?.data?.id;
        setBrandId(brandId);
        handleSaveBrandPreference(brandId)

        if (!brandId) {
          throw new Error("Brand ID missing from brand info response.");
        }
        // Step 2: Save digital info
        const cleanedSocialLinks = socialLinks.filter(
          (link: any) => link.url.trim() !== ""
        );

        const cleanedMarketplaces = marketplaces.filter(
          (marketplace: any) => marketplace.url.trim() !== ""
        );

        const brandDigitalInfo = {
          brand_id: brandId,
          social_media: cleanedSocialLinks.length > 0 ? cleanedSocialLinks : undefined,
          market_place: cleanedMarketplaces.length > 0 ? cleanedSocialLinks : undefined,
        };
        const digitalInfoRes = await SaveBrandDigitalInfo(brandDigitalInfo);


        message.success("Brand setup completed successfully!");
        props.setDefaultTab("engagement-terms");
      } catch (error: any) {
        console.error("Failed to submit brand setup:", error);
        message.error("Something went wrong. Please try again.");
      }
    }
  };

  const handleSaveBrandPreference = async (brand: string | undefined) => {

    const market_preference = marketList
      .filter((region: any) => selectedMarkets[region.name])
      .map((region: any) => ({
        region: region.id,
        regulatory_compliance:
          !!checkedItems[`${region.name}-Regulatory Compliance`],
        importation_clearance:
          !!checkedItems[`${region.name}-Importation & Clearance`],
        marketing: !!checkedItems[`${region.name}-Marketing`],
        retail_distribution: !!checkedItems[`${region.name}-Distribution`],
        offline_retail: !!checkedItems[`${region.name}-Offline Retail`],
        online: !!checkedItems[`${region.name}-Online`],
        food_service: !!checkedItems[`${region.name}-Food Service/Out of Home`],
        ambient_warehousing: !!checkedItems[`${region.name}-Ambient`],
        cold_and_frozen_warehousing:
          !!checkedItems[`${region.name}-Cold & Frozen`],
        sales: !!checkedItems[`${region.name}-Sales`],
      }));

    const payload = {
      brand_id: brand,
      engagement_model: [selectedEngagementModel],
      exclusivity_tenure: selectedExclusivityTenure,
      fob_preference: [selectedFobCifPreference],
      marketing_investments: selectedMarketingInvestment,
      market_preference: market_preference,
    };
    const brandPreferencesRes = await UpdateBrandPreference(
      payload,
      brand
    );
    // props.setDefaultTab("expansion-setup");
    navigate(-1);
  };

  const hasAnyChecked = (regionName: string) => {

    const keysToCheck = [
      "Regulatory Compliance",
      "Importation & Clearance",
      "Marketing",
      "Distribution",
      "Offline Retail",
      "Online",
      "Food Service/Out of Home",
      "Ambient",
      "Cold & Frozen",
      "Sales"
    ];

    return keysToCheck.some(key => checkedItems[`${regionName}-${key}`]);
  }

  const toggleMarketSelection = (market: string) => {
    setSelectedMarkets((prev) => ({
      ...prev,
      [market]: !prev[market],
    }));
  };

  const toggleSelectAllMarkets = () => {
    const allSelected = marketOptions.every((m) => selectedMarkets[m?.value]);

    const newState = marketOptions.reduce(
      (acc, market) => ({ ...acc, [market?.value]: !allSelected }),
      {}
    );
    setSelectedMarkets(newState);
  };

  const allSelected = marketOptions.every((m) => selectedMarkets[m?.value]);
  const someSelected = marketOptions.some((m) => selectedMarkets[m?.value]);

  const columnsList = [
    "Offline Retail",
    "Online",
    "Food Service/Out of Home",
    "Regulatory Compliance",
    "Importation & Clearance",
    "Warehousing",
    "Distribution",
    "Sales",
    "Marketing",
    "Cold & Frozen", "Ambient"
  ];

  const handleColumnCheckboxChange = (column: string, value: boolean) => {
    const updated = { ...checkedItems };
    marketOptions.forEach((market) => {
      updated[`${market?.value}-${column}`] = value;
    });
    setCheckedItems(updated);
  };

  const columnWithCheckbox = (column: string) => {
    const total = marketOptions.length;
    const checkedCount = marketOptions.filter(
      (m) => checkedItems[`${m?.value}-${column}`]
    ).length;

    return (
      <div className="flex items-center gap-2">
        <Checkbox
          checked={checkedCount === total}
          indeterminate={checkedCount > 0 && checkedCount < total}
          onChange={(e) => handleColumnCheckboxChange(column, e.target.checked)}
        />
        {column}
      </div>
    );
  };

  const tableColumns = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={toggleSelectAllMarkets}
        >
          Markets
        </Checkbox>
      ),
      dataIndex: "market",
      width: 150,
      render: (text: string) => (
        <Checkbox
          checked={selectedMarkets[text]}
          onChange={() => {
            const newSelected = !selectedMarkets[text];
            setSelectedMarkets((prev) => ({
              ...prev,
              [text]: newSelected,
            }));

            if (!newSelected) {
              const updatedCheckedItems = { ...checkedItems };
              columnsList.forEach((col) => {
                delete updatedCheckedItems[`${text}-${col}`];
              });
              setCheckedItems(updatedCheckedItems);
            }
          }}
        >
          {text}
        </Checkbox>
      ),
    },
    {
      title: "Key Channel Coverage",
      children: ["Offline Retail", "Online", "Food Service/Out of Home"].map(
        (col) => ({
          title: columnWithCheckbox(col),
          dataIndex: col,
          render: (_: any, record: { market: string }) => (
            <Checkbox
              checked={!!checkedItems[`${record.market}-${col}`]}
              onChange={() => {
                const newChecked = !checkedItems[`${record.market}-${col}`];
                const updated = {
                  ...checkedItems,
                  [`${record.market}-${col}`]: newChecked,
                };
                if (!newChecked) delete updated[`${record.market}-${col}`];
                setCheckedItems(updated);

                const isAnyChecked = columnsList.some(
                  (item) => updated[`${record.market}-${item}`]
                );
                setSelectedMarkets((prev) => ({
                  ...prev,
                  [record.market]: isAnyChecked,
                }));
              }}
            />
          ),
        })
      ),
    },
    {
      title: "Key Capabilities",
      children: [
        {
          title: "Warehousing",
          children: ["Cold & Frozen", "Ambient"].map((sub) => ({
            title: columnWithCheckbox(sub),
            dataIndex: sub,
            render: (_: any, record: { market: string }) => (
              <Checkbox
                checked={!!checkedItems[`${record.market}-${sub}`]}
                onChange={() => {
                  const newChecked = !checkedItems[`${record.market}-${sub}`];
                  const updated = {
                    ...checkedItems,
                    [`${record.market}-${sub}`]: newChecked,
                  };
                  if (!newChecked) delete updated[`${record.market}-${sub}`];
                  setCheckedItems(updated);

                  const isAnyChecked = columnsList.some(
                    (item) => updated[`${record.market}-${item}`]
                  );
                  setSelectedMarkets((prev) => ({
                    ...prev,
                    [record.market]: isAnyChecked,
                  }));
                }}
              />
            ),
          })),
        },
        ...["Distribution", "Sales", "Marketing"].map((col) => ({
          title: columnWithCheckbox(col),
          dataIndex: col,
          render: (_: any, record: { market: string }) => (
            <Checkbox
              checked={!!checkedItems[`${record.market}-${col}`]}
              onChange={() => {
                const newChecked = !checkedItems[`${record.market}-${col}`];
                const updated = {
                  ...checkedItems,
                  [`${record.market}-${col}`]: newChecked,
                };
                if (!newChecked) delete updated[`${record.market}-${col}`];
                setCheckedItems(updated);

                const isAnyChecked = columnsList.some(
                  (item) => updated[`${record.market}-${item}`]
                );
                setSelectedMarkets((prev) => ({
                  ...prev,
                  [record.market]: isAnyChecked,
                }));
              }}
            />
          ),
        })),
      ],
    },
  ];

  const dataSource = marketList.map((region: any) => {
    const row: any = { key: region.id, market: region.name };
    columnsList.forEach((col) => {
      row[col] = null;
    });
    return row;
  });

  return (
    <div className="p-6 bg-[#F9FAFB] min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Market</h2>
        {/* <span className="text-blue-600 cursor-pointer">Add New</span> */}
      </div>

      {/* <div className="flex gap-4 overflow-x-auto mb-6">
        {countries.map((country) => (
          <div
            key={country.name}
            onClick={() => setSelectedCountry(country.name)}
            className={`min-w-[200px] cursor-pointer p-4 rounded-md border border-gray-200 ${
              selectedCountry === country.name ? "bg-blue-50" : "bg-white"
            }`}
          >
            <p className="font-medium">{country.name}</p>
            <div className="flex items-center gap-2">
              <Progress
                percent={(country.progress / country.total) * 100}
                showInfo={false}
              />
              <span>
                {country.progress}/{country.total}
              </span>
            </div>
          </div>
        ))}
      </div> */}

      <Table
        bordered
        dataSource={dataSource}
        columns={tableColumns}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <div className="flex justify-end mt-6">
        <Button className="mr-2" onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          onClick={handleSaveAndContinue}
          className="!bg-[#24a0ed]"
          type="primary"
        >
          Save & Continue
        </Button>
      </div>
    </div>
  );
};

export default ExpansionSetup;
